import React, { useState, useRef, useEffect } from "react";
import { Handle, Position } from "@xyflow/react";

export const MediaMessageNode = ({ data, id, selected = false }) => {
  const [mediaType, setMediaType] = useState(data.mediaType || "image");
  const [caption, setCaption] = useState(data.caption || "");
  const [selectedFile, setSelectedFile] = useState(data.selectedFile || null);
  const [previewUrl, setPreviewUrl] = useState(data.previewUrl || "");
  const [captionError, setCaptionError] = useState("");
  const [fileError, setFileError] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef(null);

  // Media type configurations
  const mediaTypeConfig = {
    image: {
      label: "Image",
      icon: "🖼️",
      accept: "image/*",
      extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    },
    video: {
      label: "Video",
      icon: "🎥",
      accept: "video/*",
      extensions: [".mp4", ".avi", ".mov", ".wmv", ".flv"],
    },
    audio: {
      label: "Audio",
      icon: "🎵",
      accept: "audio/*",
      extensions: [".mp3", ".wav", ".ogg", ".aac", ".flac"],
    },
    document: {
      label: "Document (PDF)",
      icon: "📄",
      accept: ".pdf",
      extensions: [".pdf"],
    },
  };

  // Sync local state with data prop changes (do not sync previewUrl unless data.previewUrl changes)
  useEffect(() => {
    if (data?.caption !== undefined && data.caption !== caption) {
      setCaption(data.caption);
    }
    if (data?.mediaType !== undefined && data.mediaType !== mediaType) {
      setMediaType(data.mediaType);
    }
    if (data?.selectedFile !== selectedFile) {
      setSelectedFile(data.selectedFile);
    }
    // Only update previewUrl if data.previewUrl changes (not on every render)
    if (data?.previewUrl !== undefined) {
      setPreviewUrl(data.previewUrl);
    }
  }, [data, caption, mediaType, selectedFile]);

  const CAPTION_MAX_LENGTH = 1024;

  // Handle caption change with validation
  const handleCaptionChange = (e) => {
    const value = e.target.value;
    setCaption(value);
    if (value.length > CAPTION_MAX_LENGTH) {
      setCaptionError(`Caption cannot exceed ${CAPTION_MAX_LENGTH} characters`);
    } else {
      setCaptionError("");
    }
    if (data.onChangeCaption) {
      data.onChangeCaption(id, value);
    }
  };

  // Handle media type change
  const handleMediaTypeChange = (e) => {
    const newType = e.target.value;
    setMediaType(newType);
    setSelectedFile(null);
    setPreviewUrl("");
    setFileError("");
    if (data.onChangeMediaType) {
      data.onChangeMediaType(id, newType);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const config = mediaTypeConfig[mediaType];
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!config.extensions.includes(fileExtension)) {
      setFileError(
        `Please select a valid ${config.label.toLowerCase()} file (${config.extensions.join(", ")})`
      );
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFileError("File size must be less than 5MB");
      return;
    }
    setSelectedFile(file);
    setFileError("");
    let localPreviewUrl = "";
    if (mediaType === "image" || mediaType === "video") {
      localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl); // Set local preview immediately
      if (data.onChangePreviewUrl) {
        data.onChangePreviewUrl(id, localPreviewUrl); // Pass up to parent
      }
    }
    if (data.onChangeFile) {
      data.onChangeFile(id, file);
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const config = mediaTypeConfig[mediaType];

  return (
    <div
      style={{
        background: selected ? "#f8f9fa" : "#f1f3f4",
        border: `1px solid ${selected ? "#6f42c1" : "#e0e0e0"}`,
        borderRadius: "6px",
        padding: "16px",
        width: "320px",
        minHeight: "280px",
        boxShadow: selected
          ? "0 4px 8px rgba(111, 66, 193, 0.3)"
          : "0 2px 4px rgba(0,0,0,0.1)",
        position: "relative",
        zIndex: selected ? 30 : 10,
      }}
    >
      {/* Menu Icon */}
      <div
        style={{
          position: "absolute",
          top: 16,
          right: 8,
          cursor: "pointer",
          fontSize: 16,
          color: selected ? "#6f42c1" : "#6c757d",
          zIndex: 1,
          userSelect: "none",
          fontWeight: "bold",
          lineHeight: "1",
        }}
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋮
      </div>
      {showMenu && (
        <div
          style={{
            position: "absolute",
            top: 30,
            right: 8,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 2,
            minWidth: "100px",
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "12px",
              borderBottom: "1px solid #eee",
              transition: "background-color 0.2s",
            }}
            onClick={data.onDelete ? () => data.onDelete(id) : undefined}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f5f5f5")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "white")
            }
          >
            🗑️ Delete
          </div>
        </div>
      )}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: selected ? "#6f42c1" : "#6c757d" }}
      />
      <div style={{ textAlign: "left" }}>
        <div
          style={{
            fontWeight: "bold",
            color: selected ? "#6f42c1" : "#6c757d",
            marginBottom: "16px",
            fontSize: "14px",
          }}
        >
          Media Message
        </div>
        <div style={{ marginBottom: "16px" }}>
          <select
            value={mediaType}
            onChange={handleMediaTypeChange}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "12px",
              backgroundColor: "white",
            }}
          >
            <option value="image">🖼️ Image</option>
            <option value="video">🎥 Video</option>
            <option value="audio">🎵 Audio</option>
            <option value="document">📄 Document (PDF)</option>
          </select>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={config.accept}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #6f42c1",
              borderRadius: "4px",
              backgroundColor: "white",
              color: "#6f42c1",
              fontSize: "12px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#6f42c1";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.color = "#6f42c1";
            }}
          >
            {selectedFile ? "📁 Change File" : `📁 Upload ${config.label}`}
          </button>
        </div>
        {fileError && (
          <div
            style={{
              color: "#dc3545",
              fontSize: "10px",
              marginBottom: "8px",
              textAlign: "left",
            }}
          >
            ⚠️ {fileError}
          </div>
        )}
        <div
          style={{
            width: "100%",
            height: "80px",
            background: "#f8f9fa",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            color: "#666",
            marginBottom: "12px",
            border: "1px solid #e9ecef",
            overflow: "hidden",
          }}
        >
          {previewUrl ? (
            mediaType === "image" ? (
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : mediaType === "video" ? (
              <video
                src={previewUrl}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
                controls
              />
            ) : (
              <div>
                {config.icon} {selectedFile ? selectedFile.name : "File Preview"}
              </div>
            )
          ) : (
            <div>{config.icon} No file selected</div>
          )}
        </div>
        <div style={{ marginBottom: "8px" }}>
          <input
            type="text"
            value={caption}
            onChange={handleCaptionChange}
            placeholder={`Add caption (optional, max ${CAPTION_MAX_LENGTH} chars)`}
            maxLength={CAPTION_MAX_LENGTH}
            style={{
              width: "100%",
              padding: "6px 8px",
              border: captionError ? "1px solid #dc3545" : "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "11px",
              backgroundColor: "white",
              boxSizing: "border-box",
            }}
          />
        </div>
        {captionError && (
          <div
            style={{
              color: "#dc3545",
              fontSize: "10px",
              marginBottom: "8px",
              textAlign: "left",
            }}
          >
            ⚠️ {captionError}
          </div>
        )}
        <div
          style={{
            fontSize: "10px",
            color: caption.length > CAPTION_MAX_LENGTH ? "#dc3545" : "#6c757d",
            textAlign: "right",
            marginBottom: "6px",
            paddingRight: "4px",
          }}
        >
          {caption.length}/{CAPTION_MAX_LENGTH}
        </div>
      </div>
    </div>
  );
};
